import Joi from '@hapi/joi';
import React from 'react';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    PanelButtons,
    PanelHeader,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { ValidatingEditPanelSaveButton, ValidatingForm, ValidatingFormInputText } from '../../validatingElements';

interface ISurveyEditFmecaInspectionFindingVerificationProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
}

interface IInspectionFinding {
    id: string;
    description: string;
}

interface ISurveyEditFmecaInspectionFindingVerificationState {
    isLoaded: boolean;
    showAddEdit: boolean;
    selectedInspectionFindingId?: undefined | string;
    inspectionFindingList: IInspectionFinding[];
}

class SurveyEditFmecaInspectionFindingVerificationComponent extends ComponentSetStatePromised<
    ISurveyEditFmecaInspectionFindingVerificationProps,
    ISurveyEditFmecaInspectionFindingVerificationState
> {
    constructor(props: ISurveyEditFmecaInspectionFindingVerificationProps) {
        super(props);
        this.state = { isLoaded: false, showAddEdit: false, inspectionFindingList: [] };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override async componentDidUpdate(prevProps: ISurveyEditFmecaInspectionFindingVerificationProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ isLoaded: false });
            await this.load();
        }
    }

    public override render() {
        if (!this.state.isLoaded) {
            return null;
        }

        return (
            <>
                <PanelBody isForm={true}>
                    <PanelHeader text={this.props.t('failureMode.findings')} />
                    <PanelTable hover={true}>
                        <TableHead>
                            <TableHeadRow>
                                <TableHeader>{this.props.t('common.description')}</TableHeader>
                            </TableHeadRow>
                        </TableHead>
                        <TableBody>
                            {this.state.inspectionFindingList.map((inspectionFinding) => (
                                <TableRow
                                    key={inspectionFinding.id}
                                    isRowSelected={inspectionFinding.id === this.state.selectedInspectionFindingId}
                                    onClick={async () => this.selectInspectionFinding(inspectionFinding.id)}
                                >
                                    <TableData>{inspectionFinding.description} </TableData>
                                </TableRow>
                            ))}
                        </TableBody>
                    </PanelTable>

                    <PanelButtons noBorderBottom={true}>
                        <PanelButtonLink
                            onClick={() => this.showAdd()}
                            text={this.props.t('common.addItem', this.props.t('failureMode.finding'))}
                            icon="fa fa-plus"
                            small={true}
                            primary={true}
                        />
                    </PanelButtons>

                    {this.state.showAddEdit && (
                        <EditPanel>
                            <ValidatingForm
                                schema={Joi.object()}
                                createUrl={`/api/surveys/${this.props.surveyId}/inspectionFindings/`}
                                createUrlQueryParameters={{ unitId: this.props.unitId }}
                                itemUrl={`/api/surveys/${this.props.surveyId}/inspectionFindings/${this.state.selectedInspectionFindingId}`}
                                id={this.state.selectedInspectionFindingId}
                                onSubmitted={async () => this.handleSubmitted()}
                            >
                                <EditPanelRow>
                                    <ValidatingFormInputText name="description" placeholder={this.props.t('common.description')} />
                                </EditPanelRow>

                                <EditPanelButtons>
                                    {this.state.selectedInspectionFindingId && (
                                        <EditPanelButton onClick={async () => this.remove()} text={this.props.t('common.remove')} small={true} />
                                    )}
                                    <EditPanelButton
                                        onClick={() => this.setState({ showAddEdit: false })}
                                        text={this.props.t('common.cancel')}
                                        small={true}
                                    />
                                    <ValidatingEditPanelSaveButton small={true} />
                                </EditPanelButtons>
                            </ValidatingForm>
                        </EditPanel>
                    )}
                </PanelBody>
            </>
        );
    }

    private async selectInspectionFinding(id: string) {
        await this.setStatePromised({ selectedInspectionFindingId: id, showAddEdit: true });
    }

    private async handleSubmitted() {
        await this.setStatePromised({ showAddEdit: false });
        await this.load();
    }

    private showAdd() {
        this.setState({ showAddEdit: true, selectedInspectionFindingId: undefined });
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}/inspectionFindings/`;
        const result = await fetchGet(url, { unitId: this.props.unitId });
        await this.setStatePromised({ inspectionFindingList: result.json.items, isLoaded: true });
    }

    private async remove() {
        await fetchDelete(`/api/surveys/${this.props.surveyId}/inspectionFindings/${this.state.selectedInspectionFindingId}`);
        await this.setStatePromised({ showAddEdit: false, selectedInspectionFindingId: undefined });
        await this.load();
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyEditFmecaInspectionFindingVerification = translate()(SurveyEditFmecaInspectionFindingVerificationComponent);
